import { useQuery, UseQueryResult } from '@tanstack/react-query';

import { getIntegrationStatus } from '@/modules/common/api/integration-status';
import { FeatureMode } from '@/modules/common/components/templates/feature-details';
import { IntegrationInfoResponse } from '@/modules/common/types/dto/response/integration-response';
import { EntityType } from '@/modules/common/types/entity-type';
import { IntegrationStatusDescriptor } from '@/modules/common/types/integration-status';
import { IntegrationServices } from '@/modules/common/types/integrations';
import { getIntegrationIcon } from '@/modules/common/utils/integration-icons';

import { getAvailableTeamMembersIntegrations } from '../api/integrations.api';
import { TeamMemberIntegration } from '../types/team-member-integration';

export interface UseTeamMemberIntegrationsParams {
  mode: FeatureMode;
  teamMemberId?: string;
  rawIntegrations?: IntegrationInfoResponse[];
}

/**
 * Maps an integration status descriptor to a TeamMemberIntegration with allMembersLinked=true.
 * Used when /unlinked returns empty in Create mode — the integration is connected but all
 * external members are already linked to other team members in the app.
 */
const mapStatusDescriptorToTeamMemberIntegration = (
  descriptor: IntegrationStatusDescriptor
): TeamMemberIntegration => {
  const service = descriptor.integration as IntegrationServices;
  return {
    id: service,
    type: service,
    icon: getIntegrationIcon(service),
    teamMembers: [],
    selectedValue: null,
    allMembersLinked: true,
  };
};

/**
 * Hook to fetch available integrations for team members.
 * In Create mode, fetches only unlinked integrations. When the unlinked list is empty,
 * falls back to /api/integrations/status to show integrations that have all members
 * already linked (with a disabled selector and informational tooltip).
 * In Edit/Read mode, merges unlinked integrations with selected ones from team member data.
 */
export const useTeamMemberIntegrations = ({
  mode,
  teamMemberId,
  rawIntegrations,
}: UseTeamMemberIntegrationsParams): UseQueryResult<TeamMemberIntegration[], Error> => {
  return useQuery({
    queryKey: [
      'available-team-member-integrations',
      mode,
      teamMemberId,
      (rawIntegrations ?? []).map(i => ({
        p: i.integrationProviderType,
        c: i.connectedIntegration?.id ?? null,
      })),
    ],
    queryFn: async () => {
      const selectedIntegrations = mode !== FeatureMode.Create ? rawIntegrations : undefined;
      const unlinked = await getAvailableTeamMembersIntegrations(selectedIntegrations);

      if (mode === FeatureMode.Create && unlinked.length === 0) {
        const statusData = await getIntegrationStatus();

        // Keep only integrations that support the USER entity type
        const memberIntegrations = statusData.integrations.filter(descriptor =>
          descriptor.entities.some(entity => entity.entityType === EntityType.User)
        );

        return memberIntegrations
          .map(mapStatusDescriptorToTeamMemberIntegration)
          .sort((a, b) => a.type.localeCompare(b.type));
      }

      return unlinked;
    },
    retry: 2,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};
